import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const containerStyle = { maxWidth: 900, margin: '40px auto', padding: 24, border: '1px solid #ddd', borderRadius: 10, background: '#fafafa' };
const inputStyle = { width: '100%', padding: 10, marginBottom: 8, boxSizing: 'border-box', border: '1px solid #ccc', borderRadius: 6, fontSize: 14 };
const labelStyle = { display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 14 };
const btnPrimary = { padding: '10px 16px', background: '#28a745', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' };
const btnSecondary = { padding: '10px 16px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', marginRight: 8 };
const API_BASE = process.env.REACT_APP_API_BASE || '/api';

// ✅ Reference data from your tag_codes.csv for English
const englishQuestionTags = [
  { code: 'E-S-T1', meaning: 'Finding Key Information' },
  { code: 'E-S-T2', meaning: 'Understanding Word Meaning' },
  { code: 'E-S-T3', meaning: 'Recognising Similar Meaning' },
  { code: 'E-S-C1', meaning: 'Grammar Accuracy' },
  { code: 'E-S-P1', meaning: 'Understanding Question Requirements' },
  { code: 'E-S-P2', meaning: 'Understanding Sentence Structure' },
  { code: 'E-S-P3', meaning: 'Connecting Ideas Across Text' },
  { code: 'E-S-R1', meaning: 'Making Logical Conclusions' },
  { code: 'E-S-R2', meaning: 'Understanding Deeper Meaning' }
];

export default function EnglishQuestionUpload() {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [level, setLevel] = useState('');
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '', details: null });

  const handleFileChange = (e) => setFile(e.target.files[0]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file || !level) {
      setStatus({ type: 'error', message: 'Please select a CSV file and choose a level.' });
      return;
    }
    if (!file.name.endsWith('.csv')) {
      setStatus({ type: 'error', message: 'Please upload a valid .csv file.' });
      return;
    }

    setUploading(true);
    setStatus({ type: '', message: '', details: null });

    const formData = new FormData();
    formData.append('csvFile', file);
    formData.append('level', level);

    try {
      const res = await fetch(`${API_BASE}/admin/upload/english-questions`, {
        method: 'POST',
        body: formData
      });
      const data = await res.json();
      
      if (!res.ok) throw new Error(data.error || 'Upload failed');
      
      let message = `✅ Processed ${data.total_processed} questions:`;
      if (data.inserted > 0) message += ` ${data.inserted} new added`;
      if (data.duplicates_skipped > 0) message += `, ${data.duplicates_skipped} duplicates skipped`;
      if (data.errors > 0) message += `, ${data.errors} errors`;
      
      setStatus({ 
        type: 'success', 
        message, 
        details: data 
      });
      
      if (data.errors === 0) {
        setFile(null); 
        setLevel('');
      }
    } catch (err) {
      setStatus({ type: 'error', message: `❌ ${err.message}` });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={containerStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0 }}>📤 Upload English Questions</h2>
        <button onClick={() => navigate('/admin/dashboard')} style={btnSecondary}>← Back</button>
      </div>

      {status.message && (
        <div style={{ 
          padding: 12, 
          background: status.type === 'success' ? '#d4edda' : '#f8d7da', 
          color: status.type === 'success' ? '#155724' : '#721c24', 
          borderRadius: 6, 
          marginBottom: 15 
        }}>
          <strong>{status.message}</strong>
          {status.details && status.details.errors && status.details.errors.length > 0 && (
            <div style={{ marginTop: 10, fontSize: 12, borderTop: '1px solid rgba(0,0,0,0.1)', paddingTop: 8 }}>
              <strong>Errors:</strong>
              <ul style={{ margin: '5px 0 0 20px', padding: 0 }}>
                {status.details.errors.map((err, i) => (
                  <li key={i} style={{ marginBottom: 2 }}>{err}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div style={{ background: '#fff', padding: 20, borderRadius: 8, border: '1px solid #ddd' }}>
        <h3 style={{ marginTop: 0 }}>CSV Upload</h3>
        <p style={{ color: '#666', fontSize: 14, marginBottom: 15 }}>
          Upload a CSV file with your questions. <a href="/templates/EnglishGrade3A1.csv" download style={{ color: '#007BFF' }}>Download Template CSV</a>
        </p>

        <form onSubmit={handleUpload}>
          <label style={labelStyle}>Target Level *</label>
          <select style={inputStyle} value={level} onChange={e => setLevel(e.target.value)}>
            <option value="">Select Level (1-12)</option>
            {['1','2','3','4','5','6','7','8','9','10','11','12'].map(l => <option key={l} value={l}>Level {l}</option>)}
          </select>

          <label style={labelStyle}>CSV File *</label>
          <input type="file" accept=".csv" onChange={handleFileChange} style={{ ...inputStyle, padding: 8 }} />

          {/* ✅ UPDATED: New Required Columns List */}
          <div style={{ marginTop: 15, background: '#f8f9fa', padding: 15, borderRadius: 6, fontSize: 13, color: '#555', lineHeight: 1.6 }}>
            <strong>Required CSV Columns:</strong><br/>
            <code style={{ display: 'block', background: '#e9ecef', padding: 8, borderRadius: 4, marginTop: 5, wordBreak: 'break-all' }}>
              band1, band2, question_text, image_url, question_tag_1, question_tag_2, question_tag_3, 
              answer_a, answer_a_tag1, answer_a_tag2, answer_a_tag3, 
              answer_b, answer_b_tag1, answer_b_tag2, answer_b_tag3, 
              answer_c, answer_c_tag1, answer_c_tag2, answer_c_tag3, 
              answer_d, answer_d_tag1, answer_d_tag2, answer_d_tag3, 
              correct_answer
            </code>
            <br/>
            <em>💡 Tips:</em><br/>
            • Use <code>-</code> or leave empty for unused tags (tags are auto-truncated to 10 characters).<br/>
            • <code>image_url</code> is optional. Leave blank or use <code>-</code> if no image.<br/>
            • <code>correct_answer</code> must be A, B, C, or D.<br/>
            • <strong>Duplicate Prevention:</strong> Questions with the same text + level will be skipped.<br/>
            • The <code>level</code> column is set automatically via the dropdown above.
          </div>

          {/* ✅ NEW: Tag Reference Guide */}
          <details style={{ marginTop: 15, background: '#e7f1ff', padding: 15, borderRadius: 6, border: '1px solid #b6d4fe' }}>
            <summary style={{ fontWeight: 600, color: '#0056b3', cursor: 'pointer', fontSize: 14 }}>
              📖 Click to view valid English Question Tags
            </summary>
            <div style={{ marginTop: 10, fontSize: 13, color: '#333' }}>
              <p style={{ margin: '0 0 10px 0' }}>Use these exact codes in your <code>question_tag</code> or <code>answer_tag</code> columns:</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 8 }}>
                {englishQuestionTags.map(tag => (
                  <div key={tag.code} style={{ background: '#fff', padding: 8, borderRadius: 4, border: '1px solid #b6d4fe' }}>
                    <strong style={{ color: '#0056b3' }}>{tag.code}</strong>: {tag.meaning}
                  </div>
                ))}
              </div>
              <p style={{ marginTop: 10, fontSize: 12, color: '#666' }}>
                <em>Note: For wrong answers, you can also use error tags (e.g., M-E-C1 for Concept Misunderstanding) following a similar pattern.</em>
              </p>
            </div>
          </details>

          <button type="submit" disabled={uploading || !file || !level} style={{ ...btnPrimary, marginTop: 20, opacity: (uploading || !file || !level) ? 0.6 : 1 }}>
            {uploading ? '⏳ Processing...' : '🚀 Upload Questions'}
          </button>
        </form>
      </div>
    </div>
  );
}